<script setup lang="ts">
import { computed } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'
import {
  isStagePipelineComplete,
  LIFECYCLE_STEPS,
  stageToneOf,
} from '../../constants/listingRequestLifecycle'

// Compact inline lifecycle tracker for a list row — the same backend-driven
// model as the detail RequestStageProgress (`stageStep` 1-4 for done/active/upcoming;
// `stageState` tints the active step for attention/negative), rendered as
// Figma-style numbered pills. When the request needs attention or was closed,
// the owning note (revision / rejection reason) expands beneath the pills.
const props = defineProps<{
  currentStep: number
  state: string
  note?: string | null
}>()

type StepState = 'done' | 'active' | 'upcoming'

const tone = computed(() => stageToneOf(props.state))

function stateOf(step: number): StepState {
  // A finished pipeline has no active step — the last node is done, not pending.
  if (isStagePipelineComplete(props.state)) return step <= props.currentStep ? 'done' : 'upcoming'
  if (step < props.currentStep) return 'done'
  if (step === props.currentStep) return 'active'
  return 'upcoming'
}

// The one step whose label survives when the rail is too narrow for five of
// them (see the @container rule below). Normally that's the step in progress; a
// finished pipeline has no active step, so the final one carries the label —
// otherwise a completed request would render five wordless green ticks.
const labelledStep = computed(() =>
  isStagePipelineComplete(props.state) ? LIFECYCLE_STEPS.length : props.currentStep,
)

const showNote = computed(() => tone.value !== 'normal' && Boolean(props.note))
</script>

<template>
  <Box direction="col" gap="2" class="tracker">
    <Box flex align="center" class="tracker__rail">
      <template v-for="item in LIFECYCLE_STEPS" :key="item.step">
        <Box
          flex
          align="center"
          gap="2"
          class="tracker__pill"
          :class="[
            `tracker__pill--${stateOf(item.step)}`,
            `tracker__pill--tone-${tone}`,
            { 'tracker__pill--labelled': item.step === labelledStep },
          ]"
        >
          <Box flex align="center" justify="center" class="tracker__badge">
            <PiIcon v-if="stateOf(item.step) === 'done'" name="check" class="tracker__badge-icon" />
            <PiIcon
              v-else-if="stateOf(item.step) === 'active' && tone === 'negative'"
              name="times"
              class="tracker__badge-icon"
            />
            <PiIcon
              v-else-if="stateOf(item.step) === 'active' && tone === 'attention'"
              name="exclamation-triangle"
              class="tracker__badge-icon"
            />
            <Text v-else is="span" type="caption" weight="semibold">{{ item.step }}</Text>
          </Box>
          <Text is="span" type="caption" weight="semibold" class="tracker__label">
            {{ stateOf(item.step) === 'done' ? item.doneLabel : item.label }}
          </Text>
        </Box>
      </template>
    </Box>

    <Box v-if="showNote" class="tracker__note" :class="`tracker__note--${tone}`">
      <Text type="caption" :color="tone === 'negative' ? 'danger' : 'warning'">{{ note }}</Text>
    </Box>
  </Box>
</template>

<style scoped>
.tracker {
  width: 100%;
  /* The rail's fit depends on the width of THIS column, not the viewport — the
     row's identity block and button are fixed, so the same viewport gives the
     tracker different widths on the list page vs the dashboard. Container
     queries read the real constraint; a media query would only guess at it. */
  container-type: inline-size;
}

/* One row, always. Wrapping put "Listed" on a second line and made the request
   look like it had two trackers; the collapse rule below is what keeps this
   honest at narrow widths instead of letting the rail overflow. */
.tracker__rail {
  flex-wrap: nowrap;
  gap: 0.375rem;
}

.tracker__pill {
  padding: 0.25rem 0.5rem 0.25rem 0.3125rem;
  border: 1px solid var(--p-content-border-color);
  border-radius: 999px;
  background: var(--p-surface-0);
  flex-shrink: 0;
}

.tracker__badge {
  width: 1.25rem;
  height: 1.25rem;
  border-radius: 50%;
  border: 1px solid var(--p-content-border-color);
  color: var(--p-text-muted-color);
  flex-shrink: 0;
}

.tracker__badge-icon {
  font-size: 0.625rem;
}

.tracker__label {
  white-space: nowrap;
}

/* Done — green */
.tracker__pill--done {
  border-color: var(--p-success-200, var(--p-success-300));
  background: var(--p-success-50);
}

.tracker__pill--done .tracker__badge {
  background: var(--p-success-500);
  border-color: var(--p-success-500);
  color: var(--p-surface-0);
}

/* Active — brand by default */
.tracker__pill--active.tracker__pill--tone-normal {
  border-color: var(--p-primary-300);
  background: var(--p-primary-50);
}

.tracker__pill--active.tracker__pill--tone-normal .tracker__badge {
  border-color: var(--p-primary-500);
  color: var(--p-primary-600);
}

/* Active — attention (amber) */
.tracker__pill--active.tracker__pill--tone-attention {
  border-color: var(--p-warning-300);
  background: var(--p-warning-50);
}

.tracker__pill--active.tracker__pill--tone-attention .tracker__badge {
  background: var(--p-warning-500);
  border-color: var(--p-warning-500);
  color: var(--p-surface-0);
}

/* Active — negative (red) */
.tracker__pill--active.tracker__pill--tone-negative {
  border-color: var(--p-danger-300);
  background: var(--p-danger-50);
}

.tracker__pill--active.tracker__pill--tone-negative .tracker__badge {
  background: var(--p-danger-500);
  border-color: var(--p-danger-500);
  color: var(--p-surface-0);
}

/* Below this width five labelled pills can't share a line. Rather than wrap or
   truncate — one hides the tail of the pipeline, the other produces "Ownership
   che…" — every step except the current one drops to its badge. The seller
   still reads position (ticks behind, number ahead) and the one label that
   answers "where is it now"; the detail screen carries the full labelled
   stepper. */
@container (max-width: 640px) {
  .tracker__pill:not(.tracker__pill--labelled) {
    padding: 0.25rem;
    gap: 0;
  }

  .tracker__pill:not(.tracker__pill--labelled) .tracker__label {
    display: none;
  }

  /* Last resort on a phone-width rail: the one surviving label gives up its
     tail rather than pushing the row wider than the card. */
  .tracker__pill--labelled {
    flex: 0 1 auto;
    min-width: 0;
  }

  .tracker__pill--labelled .tracker__label {
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.tracker__note {
  padding: 0.625rem 0.75rem;
  border-radius: var(--p-border-radius-md);
}

.tracker__note--attention {
  background: var(--p-warning-50);
  border: 1px solid var(--p-warning-300);
}

.tracker__note--negative {
  background: var(--p-danger-50);
  border: 1px solid var(--p-danger-300);
}
</style>
